import { useTranslation } from "react-i18next";
import { Calendar, ArrowRight2, Edit2 } from "iconsax-react";
import { Badge, Button } from "@/shared/ui";
import { formatDateForDisplay } from "@/shared/utils";
import { getAdvanceStatusConfig } from "@/features/accounting/advances";
import type { SelfServiceRequest } from "../types";
import { getStatusBadgeColor, getStatusIcon, resolveLeaveRequestStatusLabel } from "../../shared/utils/statusUtils";
import { isRevisionRequested } from "../utils/selfRequestRules";
import { REQUEST_TYPE_ICON_MAP } from "../consts";

interface Props {
  request: SelfServiceRequest;
  locale: "ru" | "kk" | "en";
  onViewDetails: (request: SelfServiceRequest) => void;
}

export default function RequestCard({ request, locale, onViewDetails }: Props) {
  const { t } = useTranslation("EmployeePortal");
  const { t: tAdvances } = useTranslation("AdvancesPage");

  function getTypeLabel(): string {
    if (request.type === "advance" && request.details?.kind) {
      if (request.details.kind === "salary") return t("requests.types.advanceSalary");
      if (request.details.kind === "employee") return t("requests.types.advanceEmployee");
    }
    if (request.type_label) {
      const label = request.type_label[locale] || request.type_label.ru;
      if (label) return label;
    }
    const typeMap: Record<string, string> = {
      annual_leave: t("requests.types.annualLeave"),
      medical_leave: t("requests.types.medicalLeave"),
      unpaid_leave: t("requests.types.unpaidLeave"),
      amendment: t("requests.types.amendment"),
      resignation: t("requests.types.resignation"),
      completion_act: t("requests.types.completionAct"),
      unit_request: t("requests.types.itemRequest"),
    };
    return typeMap[request.type] || request.type;
  }

  function getStatusLabel(): string {
    const leaveLabel = resolveLeaveRequestStatusLabel(
      request.type,
      request.status,
      request.date_range?.start_date,
      t,
    );
    if (leaveLabel) return leaveLabel;

    if (request.status_label) {
      const label = request.status_label[locale] || request.status_label.ru;
      if (label && label !== request.status) return label;
    }
    const statusMap: Record<string, string> = {
      draft: t("requests.status.draft"),
      signing_pending: t("requests.status.pending"),
      app_approved: t("requests.status.approved"),
      order_pending: t("requests.status.orderPending"),
      order_uploaded: t("requests.status.orderUploaded"),
      order_acknowledged: t("requests.status.orderUploaded"),
      pending: t("requests.status.pending"),
      approved: t("requests.status.approved"),
      fulfilling: t("requests.status.fulfilling"),
      active: t("requests.status.active"),
      agr_pending: t("requests.status.pending"),
      pending_review: t("requests.status.pending"),
      pending_signing: t("requests.status.pending"),
      completed: t("requests.status.completed"),
      cancelled: t("requests.status.cancelled"),
      fulfilled: t("requests.status.fulfilled"),
      rejected: t("requests.status.rejected"),
      applied: t("requests.status.applied"),
      signed: t("requests.status.signed"),
      paid: t("requests.status.paid"),
      agreement_signed: t("requests.status.signed"),
      revoked: t("requests.status.cancelled"),
    };
    return statusMap[request.status] || request.status;
  }

  const revision = isRevisionRequested(request);
  const advanceConfig =
    request.type === "advance" || request.type === "business_trip" ? getAdvanceStatusConfig(request.status) : null;
  const badgeColor = advanceConfig
    ? revision
      ? "notice"
      : advanceConfig.color
    : revision
      ? "notice"
      : getStatusBadgeColor(request.status);
  const badgeText = advanceConfig
    ? revision
      ? tAdvances("statusLabels.revision")
      : tAdvances(advanceConfig.label)
    : revision
      ? t("requests.status.revisionRequested")
      : getStatusLabel();
  const badgeIcon = advanceConfig
    ? revision
      ? <Edit2 size={14} color="currentColor" />
      : advanceConfig.icon
    : revision
      ? <Edit2 size={12} color="currentColor" />
      : getStatusIcon(request.status);

  const TypeIcon = REQUEST_TYPE_ICON_MAP[request.type] || Calendar;
  return (
    <div className="flex flex-col gap-4 radius-lg border surface-base-stroke surface-base-fill p-5">
      <Badge variant="soft" color={badgeColor} text={badgeText} icon={badgeIcon} />
      <div className="flex items-center gap-2">
        <div className="w-10 aspect-square radius-xs surface-component-fill content-action-neutral flex items-center justify-center">
          <TypeIcon size={20} color="currentColor" />
        </div>
        <span className="text-body-bold-lg content-base-primary">{getTypeLabel()}</span>
      </div>

      <div className="border-t surface-base-stroke" />

      <div className="flex items-center gap-1.5">
        <Calendar size={16} color="currentColor" className="content-action-brand" />
        <span className="text-body-regular-sm content-base-secondary">
          {t("requests.card.created")}: {formatDateForDisplay(request.created_at, true)}
        </span>
      </div>

      {request.type === "amendment" && request.details?.effective_date && (
        <div className="flex items-center gap-1.5">
          <Calendar size={16} color="currentColor" className="content-action-brand" />
          <span className="text-body-regular-sm content-base-secondary">
            {t("requests.card.effectiveDate")}: {formatDateForDisplay(request.details.effective_date)}
          </span>
        </div>
      )}

      <div className="flex items-center justify-end">
        <Button variant="secondary" size="md" onClick={() => onViewDetails(request)}>
          {t("requests.card.viewDetails")}
          <ArrowRight2 size={14} color="currentColor" />
        </Button>
      </div>
    </div>
  );
}
